import { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { apiGet, apiPost, apiPut, apiDelete } from '../shared/api'
import { useI18n } from '../i18n'

interface User {
  id: string
  email: string
  displayName: string
  subscription?: {
    planId: string
    expiresAt: string
    status: string
  }
  createdAt: string
}

interface Site {
  id: string
  name: string
  baseUrl: string
  type: string
  status: 'active' | 'disabled'
  createdAt: string
}

interface GlobalSettings {
  disableAds: boolean
  pricing?: {
    monthly: number
    yearly: number
    currency: string
  }
}

export default function Admin() {
  const { t } = useI18n()
  const [activeTab, setActiveTab] = useState<'users' | 'sites' | 'settings'>('users')
  const [users, setUsers] = useState<User[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>({ disableAds: false, pricing: { monthly: 1.99, yearly: 19.9, currency: 'USD' } })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const { me, setMe } = useOutletContext<{ me: { id: string; displayName: string; role?: 'user' | 'admin' } | null; setMe: (u: any) => void }>()

  // admin login form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState('')

  // 用户管理状态
  const [showAddUser, setShowAddUser] = useState(false)
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', planId: 'free' })

  // 站点管理状态
  const [showAddSite, setShowAddSite] = useState(false)
  const [newSite, setNewSite] = useState({ name: '', baseUrl: '', type: 'generic' })

  // 父布局会在应用启动时拉取 `me`，此处无需重复获取

  useEffect(() => {
    if (me?.role === 'admin') {
      loadData()
    }
  }, [me])

  const loadData = async () => {
    setLoading(true)
    try {
      const [usersRes, sitesRes, settingsRes] = await Promise.all([
        apiGet<{ users: any[] }>('/api/admin/users'),
        apiGet<{ sites: any[] }>('/api/admin/sites'),
        apiGet<{ settings: GlobalSettings }>('/api/admin/settings')
      ])
      
      setUsers(usersRes.users || [])
      setSites(sitesRes.sites || [])
      setGlobalSettings(settingsRes.settings || { disableAds: false, pricing: { monthly: 1.99, yearly: 19.9, currency: 'USD' } })
    } catch (error) {
      console.error('Failed to load admin data:', error)
      setMessage(t('admin.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password.trim()) {
      setAuthError(t('auth.fillAllFields'))
      return
    }
    setAuthLoading(true)
    setAuthError('')
    try {
      const res = await apiPost<{ ok: boolean; user?: { id: string; displayName: string; role?: 'user' | 'admin' } }>('/api/auth/login', { email, password })
      if (res?.user?.role === 'admin') {
        setMe(res.user)
        // load admin data after login
        await loadData()
      } else {
        setAuthError(t('admin.needAdmin'))
      }
    } catch (err: any) {
      setAuthError(err?.message || t('auth.loginFailed'))
    } finally {
      setAuthLoading(false)
    }
  }

  // 用户管理功能
  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.displayName) {
      setMessage(t('auth.fillAllFields'))
      return
    }

    setLoading(true)
    try {
      const res = await apiPost<{ success: boolean; error?: string }>('/api/admin/users', newUser)
      if (res.success) {
        setMessage(t('admin.userAdded'))
        setShowAddUser(false)
        setNewUser({ email: '', password: '', displayName: '', planId: 'free' })
        await loadData()
      } else {
        setMessage(res.error || t('admin.adding'))
      }
    } catch (error) {
      setMessage(t('admin.adding'))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('admin.deleteConfirm'))) return

    setLoading(true)
    try {
      const res = await apiDelete<{ success: boolean; error?: string }>('/api/admin/users', { userId })
      if (res.success) {
        setMessage(t('admin.userDeleted'))
        await loadData()
      } else {
        setMessage(res.error || t('admin.delete'))
      }
    } catch (error) {
      setMessage(t('admin.delete'))
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateUserSubscription = async (userId: string, planId: string) => {
    setLoading(true)
    try {
      const res = await apiPut<{ success: boolean; error?: string }>('/api/admin/users', { userId, planId })
      if (res.success) {
        setMessage(t('subscription.subscribeSuccess'))
        await loadData()
      } else {
        setMessage(res.error || t('subscription.subscribeFailed'))
      }
    } catch (error) {
      setMessage(t('subscription.subscribeFailed'))
    } finally {
      setLoading(false)
    }
  }

  // 站点管理功能
  const handleAddSite = async () => {
    if (!newSite.name || !newSite.baseUrl) {
      setMessage(t('auth.fillAllFields'))
      return
    }

    setLoading(true)
    try {
      const res = await apiPost<{ success: boolean; error?: string }>('/api/admin/sites', newSite)
      if (res.success) {
        setMessage(t('admin.siteAdded'))
        setShowAddSite(false)
        setNewSite({ name: '', baseUrl: '', type: 'generic' })
        await loadData()
      } else {
        setMessage(res.error || t('admin.addSite'))
      }
    } catch (error) {
      setMessage(t('admin.addSite'))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSite = async (siteId: string) => {
    if (!confirm(t('admin.confirmDeleteSite'))) return

    setLoading(true)
    try {
      const res = await apiDelete<{ success: boolean; error?: string }>('/api/admin/sites', { siteId })
      if (res.success) {
        setMessage(t('admin.siteDeleted'))
        await loadData()
      } else {
        setMessage(res.error || t('admin.deleteSite'))
      }
    } catch (error) {
      setMessage(t('admin.deleteSite'))
    } finally {
      setLoading(false)
    }
  }

  const handleToggleSiteStatus = async (siteId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active'
    setLoading(true)
    try {
      const res = await apiPut<{ success: boolean; error?: string }>('/api/admin/sites', { siteId, status: newStatus })
      if (res.success) {
        setMessage(t('admin.updateSiteStatusSuccess'))
        await loadData()
      } else {
        setMessage(res.error || t('admin.updateSettings'))
      }
    } catch (error) {
      setMessage(t('admin.updateSettings'))
    } finally {
      setLoading(false)
    }
  }

  // 全局设置功能
  const handleUpdateGlobalSettings = async () => {
    setLoading(true)
    try {
      const res = await apiPost<{ success: boolean; error?: string }>('/api/admin/settings', globalSettings)
      if (res.success) {
        setMessage(t('admin.updateSettingsSuccess'))
      } else {
        setMessage(res.error || t('admin.updateSettings'))
      }
    } catch (error) {
      setMessage(t('admin.updateSettings'))
    } finally {
      setLoading(false)
    }
  }

  const getSubscriptionPlanName = (planId: string) => {
    const plans: Record<string, string> = { free: t('admin.free'), monthly: t('admin.monthly'), yearly: t('admin.yearly') }
    return plans[planId] || planId
  }

  if (me?.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow border">
          <h1 className="text-2xl font-bold mb-6 text-center">{t('admin.adminLogin')}</h1>
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <input
              type="email"
              placeholder={t('admin.adminEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="password"
              placeholder={t('admin.adminPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {authError && (
              <div className="text-red-600 text-sm">{authError}</div>
            )}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {authLoading ? t('admin.loggingIn') : t('admin.login')}
            </button>
          </form>
        </div>
      </div>
    )
  }

  if (loading && !users.length && !sites.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('admin.title')}</h1>
        <p className="text-gray-600">{t('admin.subtitle')}</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('成功') || message.includes('success') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* 标签页导航 */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'users', name: t('admin.usersTab'), count: users.length },
            { id: 'sites', name: t('admin.sitesTab'), count: sites.length },
            { id: 'settings', name: t('admin.settingsTab') }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
              {tab.count !== undefined && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* 用户管理 */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">{t('admin.users')}</h2>
            <button
              onClick={() => setShowAddUser(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('admin.addUser')}
            </button>
          </div>

          {showAddUser && (
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('admin.addUser')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="email"
                  placeholder={t('admin.email')}
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="password"
                  placeholder={t('admin.password')}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder={t('admin.displayName')}
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <select
                  value={newUser.planId}
                  onChange={(e) => setNewUser({ ...newUser, planId: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="free">{t('admin.free')}</option>
                  <option value="monthly">{t('admin.monthly')}</option>
                  <option value="yearly">{t('admin.yearly')}</option>
                </select>
              </div>
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={handleAddUser}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? t('admin.adding') : t('admin.addUser')}
                </button>
                <button
                  onClick={() => setShowAddUser(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white shadow overflow-hidden rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.users')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.plan')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.registeredAt')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.subscription ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {getSubscriptionPlanName(user.subscription.planId)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(user.subscription.expiresAt).toLocaleDateString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">{t('admin.noSubscription')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <select
                        value={user.subscription?.planId || 'free'}
                        onChange={(e) => handleUpdateUserSubscription(user.id, e.target.value)}
                        disabled={loading}
                        className="px-2 py-1 border rounded text-xs"
                      >
                        <option value="free">{t('admin.free')}</option>
                        <option value="monthly">{t('admin.monthly')}</option>
                        <option value="yearly">{t('admin.yearly')}</option>
                      </select>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={loading}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        {t('admin.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 站点管理 */}
      {activeTab === 'sites' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">{t('admin.sites')}</h2>
            <button
              onClick={() => setShowAddSite(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('admin.addSite')}
            </button>
          </div>

          {showAddSite && (
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('admin.addSite')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder={t('admin.siteName')}
                  value={newSite.name}
                  onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="url"
                  placeholder={t('admin.baseUrl')}
                  value={newSite.baseUrl}
                  onChange={(e) => setNewSite({ ...newSite, baseUrl: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <select
                  value={newSite.type}
                  onChange={(e) => setNewSite({ ...newSite, type: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="generic">{t('admin.genericAdapter')}</option>
                  <option value="demo">{t('admin.demoAdapter')}</option>
                </select>
              </div>
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={handleAddSite}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? t('admin.adding') : t('admin.addSite')}
                </button>
                <button
                  onClick={() => setShowAddSite(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white shadow overflow-hidden rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.sites')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.type')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sites.map((site) => (
                  <tr key={site.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{site.name}</div>
                        <div className="text-sm text-gray-500">{site.baseUrl}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {site.type === 'generic' ? t('admin.genericAdapter') : t('admin.demoAdapter')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        site.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {site.status === 'active' ? t('admin.enabled') : t('admin.disabled')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleToggleSiteStatus(site.id, site.status)}
                        disabled={loading}
                        className={`px-3 py-1 rounded text-xs ${
                          site.status === 'active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } disabled:opacity-50`}
                      >
                        {site.status === 'active' ? t('admin.disabled') : t('admin.enabled')}
                      </button>
                      <button
                        onClick={() => handleDeleteSite(site.id)}
                        disabled={loading}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        {t('admin.deleteSite')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 全局设置 */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">{t('admin.globalSettings')}</h2>
          
          <div className="bg-white shadow rounded-lg p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{t('admin.disableAds')}</h3>
                  <p className="text-sm text-gray-500">{t('admin.disableAdsDesc')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={globalSettings.disableAds}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, disableAds: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('admin.pricingSettings')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.monthlyPrice')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={globalSettings.pricing?.monthly ?? 1.99}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, pricing: { ...(globalSettings.pricing || { monthly: 1.99, yearly: 19.9, currency: 'USD' }), monthly: Number(e.target.value) } })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.yearlyPrice')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={globalSettings.pricing?.yearly ?? 19.9}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, pricing: { ...(globalSettings.pricing || { monthly: 1.99, yearly: 19.9, currency: 'USD' }), yearly: Number(e.target.value) } })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.currency')}</label>
                    <input
                      type="text"
                      maxLength={3}
                      value={globalSettings.pricing?.currency ?? 'USD'}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, pricing: { ...(globalSettings.pricing || { monthly: 1.99, yearly: 19.9, currency: 'USD' }), currency: e.target.value.toUpperCase() } })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('admin.currencyDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={handleUpdateGlobalSettings}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? t('admin.saving') : t('admin.saveSettings')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
